import React, { useState } from "react";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, Loader2, ChevronDown } from "lucide-react";
import { api, errText, btnLight, inputCls, fmtDateTime } from "./api";

const addHours = (h) => new Date(Date.now() + h * 3600 * 1000);
const tomorrow8 = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(8, 0, 0, 0); return d; };
const PRESETS = [
  { key: "24h", label: "Pause 24 hours", until: () => addHours(24) },
  { key: "48h", label: "Pause 48 hours", until: () => addHours(48) },
  { key: "tomorrow8", label: "Pause until tomorrow 8:00 AM", until: tomorrow8 },
];

export function PauseBanner({ pause, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!pause?.active) return null;
  const resume = async () => {
    setBusy(true);
    try { await api.resume(); toast.success("Sending resumed — the batch continues from where it stopped"); onChanged(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const provider = /provider/i.test(pause.reason || "");
  return (
    <div className={`border-l-4 px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 ${provider ? "bg-[#FDECE6] border-[#C84C21]" : "bg-[#FFF6E5] border-[#B7791F]"}`} data-testid="pause-banner">
      <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
        <PauseCircle size={13} /> Sending paused
      </span>
      <span className="text-[13px] text-[#0F1825]" data-testid="pause-until">
        Resumes <strong>{fmtDateTime(pause.until)}</strong> (your local time) · {pause.reason}
        {provider ? " — Google refused further sends; re-queued contacts stay approved and go out after the pause." : ""}
      </span>
      {pause.manual_until && (
        <button type="button" onClick={resume} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#0F1825] hover:text-[#C84C21] disabled:opacity-50" data-testid="pause-resume">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Resume now
        </button>
      )}
    </div>
  );
}

export function PauseMenu({ pause, onChanged, disabled }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (until, label) => {
    if (!(until instanceof Date) || Number.isNaN(until.getTime()) || until.getTime() <= Date.now()) { toast.error("Pick a date and time in the future."); return; }
    setBusy(true);
    try {
      await api.pause(until.toISOString(), label);
      toast.success(`Sending paused until ${fmtDateTime(until.toISOString())}`);
      setOpen(false); setCustom(""); onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <span className="relative inline-flex" data-testid="pause-menu">
      <button type="button" disabled={disabled || busy} onClick={() => setOpen((v) => !v)} className={`${btnLight} ${pause?.active ? "border-[#C84C21] text-[#C84C21]" : ""}`} data-testid="pause-toggle" title="Pause sending for a while — approvals and the queue are kept">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <PauseCircle size={12} />} {pause?.active ? "Paused" : "Pause sending"} <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-[300px] bg-white border border-[#E8E1D2] shadow-lg p-2 space-y-1" data-testid="pause-options">
          {PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => apply(p.until(), p.label.replace("Pause", "paused by you ·").toLowerCase())} className="w-full text-left px-3 py-2 text-[12.5px] text-[#0F1825] hover:bg-[#FAF7F1]" data-testid={`pause-${p.key}`}>
              {p.label} <span className="font-mono text-[10px] text-[#A09680]">· until {fmtDateTime(p.until().toISOString())}</span>
            </button>
          ))}
          <div className="border-t border-[#F0EBDC] pt-2 px-1">
            <label className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680]">Custom date & time (your local time)</label>
            <div className="flex gap-2 mt-1">
              <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} className={`${inputCls} !py-1.5 !text-[12px]`} data-testid="pause-custom-input" />
              <button type="button" disabled={!custom} onClick={() => apply(new Date(custom), "paused by you · custom")} className={btnLight} data-testid="pause-custom-apply">Pause</button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
